import {
  getSession,
  signOut
} from '@/lib/auth-client'
import { trpcClient } from '@/lib/trpc'
import { SessionObject } from '@/stores/sessionStore'
import { createFileRoute, Outlet, redirect } from '@tanstack/react-router'
import { useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { useAuthCheck } from '../../lib/auth'
import { FeedbackWidget } from '@/components/feedback/FeedbackWidget'

// Helper functions for organization/workspace management
function getStoredActiveWorkspace() {
  try {
    const storedWorkspace = localStorage.getItem('auth:activeWorkspace');
    return storedWorkspace ? JSON.parse(storedWorkspace) : null;
  } catch (error) {
    console.error('Error parsing stored workspace:', error);
    return null;
  }
}

// Extract workspace ID from URL path
function extractWorkspaceIdFromPath(path: string): string | null {
  const match = path.match(/\/workspaces\/([^\/]+)/);
  return match ? match[1] : null;
}

async function ensureOrganizationAndWorkspaceData(session: SessionObject, targetPath: string) {
  // Check if URL contains a workspace ID
  const workspaceIdFromUrl = extractWorkspaceIdFromPath(targetPath);

  if (workspaceIdFromUrl) {
    // Skip API calls if we already have cached data for this workspace
    const storedWorkspace = getStoredActiveWorkspace();
    const storedOrgStr = localStorage.getItem('auth:activeOrg');
    const storedOrg = storedOrgStr ? JSON.parse(storedOrgStr) : null;
    const isSameWorkspace = storedWorkspace &&
      (storedWorkspace.id === workspaceIdFromUrl || storedWorkspace.slug === workspaceIdFromUrl);

    if (isSameWorkspace && storedOrg) {
      return {
        organization: storedOrg,
        workspace: storedWorkspace,
        needsReload: false,
      };
    }

    // URL-based navigation - use single API call to handle everything
    try {
      // Clear stale workspace ID to prevent middleware from rejecting
      // based on old workspace the user may have been removed from
      localStorage.setItem('auth:activeWorkspaceId', '');

      // 前置校验：用户是否为该空间成员（前端拦截，避免错误写入缓存）
      const myRole = await trpcClient.workspaceMembers.getMyRole.query({
        workspaceId: workspaceIdFromUrl,
      });

      if (!myRole?.role) {
        throw redirect({ to: '/403' });
      }

      console.log('🔍 Loading workspace context from URL:', workspaceIdFromUrl);

      const result = await trpcClient.organizations.getWorkspaceContext.query({
        workspaceId: workspaceIdFromUrl
      });

      // Cache organization and workspace data
      if (result.organization) {
        localStorage.setItem('auth:activeOrg', JSON.stringify(result.organization));
      }
      if (result.workspace) {
        localStorage.setItem('auth:activeWorkspace', JSON.stringify(result.workspace));
        localStorage.setItem('auth:activeWorkspaceId', result.workspace.id);
      }

      // Store delegation context if delegation is active (either just set or already active)
      if (result.delegationActive) {
        // Get delegation context - either from API response or construct from workspace org
        const delegationContext = result.delegationContext || {
          clientOrgId: result.workspace.organizationId,
          clientOrgName: result.organization.name,
          partnerOrgId: result.organization.parentPartnerId
        };

        localStorage.setItem('partner:delegation', JSON.stringify({
          isDelegate: true,
          ...delegationContext,
          timestamp: Date.now()
        }));

        console.log('💾 Stored delegation context:', delegationContext);
      } else {
        // Clear delegation context if not delegating
        localStorage.removeItem('partner:delegation');
      }

      // If delegation was set or org was switched, need to reload
      if (result.needsReload) {
        console.log('✅ Context updated, reload needed');

        return {
          organization: result.organization,
          workspace: result.workspace,
          needsReload: true
        };
      }

      console.log('✅ Workspace context loaded:', {
        org: result.organization?.name,
        workspace: result.workspace?.name,
        delegationActive: result.delegationActive
      });

      return {
        organization: result.organization,
        workspace: result.workspace,
        needsReload: false
      };
    } catch (error: any) {
      console.error('❌ Failed to load workspace context:', error);
      if (error?.data?.code === 'UNAUTHORIZED') {
        throw redirect({
          to: '/login',
          search: {
            error: 'not-authorized',
            redirect_uri: window.location.href,
          },
        })
      }
      if (error?.data?.code === 'FORBIDDEN' || error?.data?.code === 'NOT_FOUND') {
        throw redirect({
          to: '/403',
          search: { error: error.message || 'Workspace not accessible' }
        });
      }
      throw error;
    }
  }

  // No workspace in URL - fetch user's active organization
  if (!session.session.activeOrganizationId) {
    return { organization: null, workspace: null, needsReload: false };
  }

  try {
    const delegatedOrgId = (session.session as any)?.delegatedOrganizationId;
    const orgIdToFetch = delegatedOrgId || session.session.activeOrganizationId;

    const { organization: orgWithWorkspaces } = await trpcClient.organizations.getOrganizationWithWorkspaces.query({
      id: orgIdToFetch
    });

    let workspace = getStoredActiveWorkspace();

    if (orgWithWorkspaces) {
      localStorage.setItem('auth:activeOrg', JSON.stringify(orgWithWorkspaces));

      // Self-heal stale workspace cache when switching accounts/orgs.
      if (workspace && orgWithWorkspaces.workspaces?.length > 0) {
        const isWorkspaceInOrg = orgWithWorkspaces.workspaces.some(
          (ws: any) =>
            ws.id === workspace.id ||
            (workspace.slug && ws.slug === workspace.slug)
        );

        if (!isWorkspaceInOrg) {
          workspace = null;
          localStorage.removeItem('auth:activeWorkspace');
          localStorage.removeItem('auth:activeWorkspaceId');
        }
      }

      // Set first workspace if no workspace is stored
      if (!workspace && orgWithWorkspaces.workspaces?.length > 0) {
        workspace = orgWithWorkspaces.workspaces[0];
        localStorage.setItem('auth:activeWorkspace', JSON.stringify(workspace));
        localStorage.setItem('auth:activeWorkspaceId', workspace.id);
      }

      return { organization: orgWithWorkspaces, workspace, needsReload: false };
    }

    return { organization: null, workspace, needsReload: false };
  } catch (error) {
    console.error('Error fetching organization/workspace data:', error);
    return { organization: null, workspace: null, needsReload: false };
  }
}

export const Route = createFileRoute('/(protected)')({
  component: RouteComponent,
  beforeLoad: async ({ location }) => {
    // Handle auth tokens from URL
    const urlParams = new URLSearchParams(window.location.search);

    // Handle signed token+expiry (from OAuth callback or other server redirects)
    const tokenParam = urlParams.get('token');
    const expiryParam = urlParams.get('expiry');
    if (tokenParam && expiryParam) {
      localStorage.setItem('auth:token', tokenParam);
      localStorage.setItem('auth:expiry', expiryParam);
      urlParams.delete('token');
      urlParams.delete('expiry');
      const cleanSearch = urlParams.toString();
      const cleanUrl = window.location.pathname + (cleanSearch ? `?${cleanSearch}` : '');
      window.history.replaceState({}, '', cleanUrl);
    }

    // Handle raw extension token: exchange with server for a signed token
    const extToken = urlParams.get('ext_token');
    if (extToken) {
      // Clean ext_token from URL immediately
      urlParams.delete('ext_token');
      const cleanSearch = urlParams.toString();
      const cleanUrl = window.location.pathname + (cleanSearch ? `?${cleanSearch}` : '');
      window.history.replaceState({}, '', cleanUrl);

      try {
        const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:15001';
        const res = await fetch(`${serverUrl.replace(/\/$/, '')}/api/ext-session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: extToken }),
        });
        if (res.ok) {
          const { token: signedToken, expiry } = await res.json();
          localStorage.setItem('auth:token', signedToken);
          localStorage.setItem('auth:expiry', expiry);
        }
      } catch (e) {
        console.error('Failed to exchange extension token:', e);
      }
    }

    const storedSession = localStorage.getItem('auth:session')
    const targetPath = location.pathname;

    let session = null
    if (storedSession) {
      try {
        session = JSON.parse(storedSession) as SessionObject
        if (
          !session?.session?.expiresAt ||
          new Date(session.session.expiresAt) < new Date()
        ) {
          // already expired, try to fetch new session
          await signOut()
          session = null
        }
      } catch (error) {
        console.error('Error parsing stored session', error)
      }
    }
    if (!session) {
      // try to fetch new session
      session = await getSession()
    }

    if (!session) {
      throw redirect({
        to:
          '/login?error=session-not-found&redirect_uri=' +
          encodeURIComponent(window.location.href),
      })
    }

    if (
      !session?.session?.expiresAt ||
      new Date(session.session.expiresAt) < new Date()
    ) {
      throw redirect({
        to:
          '/login?error=session-expired&redirect_uri=' +
          encodeURIComponent(window.location.href),
      })
    }

    // Ensure organization and workspace data is available
    // This will auto-detect delegation needs from URL and set it if necessary
    const { organization, workspace, needsReload } = await ensureOrganizationAndWorkspaceData(session, targetPath);

    // If delegation was set or org was switched, reload to get fresh session
    if (needsReload) {
      console.log('🔄 Reloading to apply context changes...');

      // Clear session cache to force fresh fetch
      localStorage.removeItem('auth:session');

      // Reload the page
      window.location.reload();

      return { session, organization: null, workspace: null };
    }

    // If on root path and we have a workspace, redirect to it immediately
    // This avoids relying on the Jotai atom/component-level <Navigate> which has timing issues
    if (targetPath === '/' && workspace) {
      const workspaceIdentifier = workspace.id;
      throw redirect({ to: `/workspaces/${workspaceIdentifier}` as any });
    }

    // If user has no organization, redirect to onboarding
    if (!session.session.activeOrganizationId || !organization) {
      // Check for partner delegation context first
      const delegationContext = localStorage.getItem('partner:delegation');
      const isDelegating = delegationContext ? JSON.parse(delegationContext).isDelegate : false;

      // If partner is delegating to a client, skip the organization check
      // The partner is not a member of the client org, but has delegated access
      if (!isDelegating) {
        // Check if user has any organizations at all
        let userOrgs: any[] | null = null;
        let orgCheckError: any = null;

        try {
          const userOrgResult = await trpcClient.organizations.getUserOrganizations.query();
          userOrgs = userOrgResult?.organizations ?? [];
        } catch (error: any) {
          orgCheckError = error;
        }

        // Handle org check error
        if (orgCheckError) {
          console.error('Error checking user organizations:', orgCheckError);

          if (orgCheckError?.data?.code === 'FORBIDDEN' || orgCheckError?.data?.code === 'UNAUTHORIZED') {
            throw redirect({
              to: '/login?error=not-authorized&redirect_uri=' + encodeURIComponent(window.location.href),
            })
          }

          throw redirect({
            to: '/login?error=failed-to-load-organizations&redirect_uri=' + encodeURIComponent(window.location.href),
          })
        }

        // User has no organizations → try to auto-create one before giving up
        if (!userOrgs || userOrgs.length === 0) {
          try {
            const userName = session.user.name || session.user.email?.split('@')[0] || 'My';
            const orgName = `${userName}'s Workspace`;
            const { organization: newOrg, workspace: newWorkspace } = await trpcClient.organizations.createOrganization.mutate({
              name: orgName,
              slug: orgName,
            });
            console.log('Auto-created organization for user:', newOrg.id);

            // Set active org and workspace, then reload
            localStorage.setItem('auth:activeOrg', JSON.stringify(newOrg));
            if (newWorkspace) {
              localStorage.setItem('auth:activeWorkspace', JSON.stringify(newWorkspace));
              localStorage.setItem('auth:activeWorkspaceId', newWorkspace.id);
            }
            window.location.reload();
            return { session, organization: null, workspace: null };
          } catch (autoCreateError) {
            console.error('Failed to auto-create organization:', autoCreateError);
            throw redirect({
              to: '/login?error=account-setup-incomplete&redirect_uri=' + encodeURIComponent(window.location.href),
            })
          }
        }

        // User has organizations but none is active → set the first one
        if (userOrgs.length > 0 && !session.session.activeOrganizationId) {
          const firstOrg = userOrgs[0];

          // Try to set active org on server (non-blocking on failure)
          try {
            await trpcClient.organizations.setActiveOrganization.mutate({
              organizationId: firstOrg.id
            });
          } catch (e) {
            console.error('Failed to set active organization on server:', e);
            // Continue anyway — localStorage will handle the redirect
          }

          localStorage.setItem('auth:activeOrg', JSON.stringify(firstOrg));

          try {
            const { organization: orgWithWorkspaces } = await trpcClient.organizations.getOrganizationWithWorkspaces.query({
              id: firstOrg.id
            });

            if (orgWithWorkspaces?.workspaces?.length > 0) {
              const firstWorkspace = orgWithWorkspaces.workspaces[0];
              localStorage.setItem('auth:activeWorkspace', JSON.stringify(firstWorkspace));
              localStorage.setItem('auth:activeWorkspaceId', firstWorkspace.id);
            }
          } catch (e) {
            console.error('Error loading org workspaces:', e);
          }

          // Clear stale session so reload fetches fresh one
          localStorage.removeItem('auth:session');
          window.location.reload();
          return { session, organization: null, workspace: null };
        }
      }
    }

    return {
      session,
      organization,
      workspace,
    }
  },
})

function RouteComponent() {
  const { setupAuthListener } = useAuthCheck()
  const { session, organization, workspace } = Route.useRouteContext()

  useEffect(() => {
    setupAuthListener()
  }, [setupAuthListener])

  // Attach the active identity to Sentry so future events carry user / org /
  // workspace tags. Re-runs whenever any of those change.
  useEffect(() => {
    const user = session?.user
    if (!user?.id) {
      Sentry.setUser(null)
      Sentry.setContext('organization', null)
      Sentry.setContext('workspace', null)
      return
    }

    Sentry.setUser({
      id: user.id,
      email: user.email ?? undefined,
      username: user.name ?? undefined,
    })

    if (organization) {
      Sentry.setTag('organization.id', organization.id)
      Sentry.setContext('organization', {
        id: organization.id,
        name: organization.name,
        slug: organization.slug,
      })
    } else {
      Sentry.setContext('organization', null)
    }

    if (workspace) {
      Sentry.setTag('workspace.id', workspace.id)
      Sentry.setContext('workspace', {
        id: workspace.id,
        name: workspace.name,
        slug: workspace.slug,
      })
    } else {
      Sentry.setContext('workspace', null)
    }
  }, [
    session?.user?.id,
    session?.user?.email,
    session?.user?.name,
    organization?.id,
    organization?.name,
    organization?.slug,
    workspace?.id,
    workspace?.name,
    workspace?.slug,
  ])

  return (
    <>
      <Outlet />
      <FeedbackWidget />
    </>
  )
}
